/**
 * Who spoke when, as one track per voice against a shared clock.
 *
 * The panel sits between the legend and the transcript, so opening it pushes the reading column
 * down rather than covering it — a timeline nobody can read the words beside is a picture, not a
 * tool. It is drawn entirely from the turns already loaded (`buildSpeakerActivity`), so there is
 * no second read model and nothing to keep in step.
 *
 * **Its height is its content, and at five speakers that is the design's 164px** — five 17px rows
 * on 8px gaps, a 15px axis, 12px of padding either side. More voices make it taller rather than
 * putting a second scrollbar inside a dialog that already has one.
 *
 * A segment is a click target, and what it does is **jump**: it names its own stretch of the
 * recording and scrolls the transcript to the turn it draws, in every play state, so the panel is
 * a way *into* the words rather than a summary beside them.
 *
 * **While sound is coming out, the panel is also the transport.** Then — and only then — a
 * playhead is drawn across every track at the clock's position, everything after it is knocked
 * back, its cap can be dragged, and a press on a track seeks there and keeps playing. Silent, none
 * of that is drawn and nothing here touches the player: the panel's first job is who spoke when,
 * and a playhead parked against a paused recording is chrome over the picture that answers it. The
 * way to start playing from a passage is the turn's own play glyph in the column.
 */

import { type TranscriptSpeakerNames, transcriptSpeakerDisplayLabel } from "@repo/shared";
import { type PointerEvent as ReactPointerEvent, useCallback, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import {
  formatSpeakerActivityRange,
  formatSpeakerActivityTime,
  type SpeakerActivity,
  type SpeakerActivityRow,
} from "../lib/speaker-activity";
import { speakerPaletteStyle } from "../lib/speaker-palette";

/**
 * What the panel needs to be a transport, and nothing more.
 *
 * Not the player itself: the panel draws one position and asks for another, and handing it the
 * whole player would let a timeline decide things about playback that the dialog owns.
 */
export type TranscriptActivityTransport = {
  /** Where the playhead sits — the position asked for while a seek is still buffering. */
  positionSeconds: number;
  /**
   * Sound is coming out right now. It is the whole of the panel's transport switch: the playhead,
   * its cap, the knocked-back remainder and every seek exist while this holds and at no other
   * time.
   */
  isSounding: boolean;
  /** Seek to a point on the timeline, keeping the recording playing from there. */
  onSeek: (seconds: number) => void;
  onScrubStart: () => void;
  onScrubEnd: () => void;
};

export type TranscriptActivityPanelProps = {
  activity: SpeakerActivity;
  speakerNames: TranscriptSpeakerNames;
  /** Scroll the transcript to the turn a segment draws. */
  onJumpToTurn: (turnKey: number) => void;
  /** `null` for a recording nothing can play: the panel then never becomes a transport at all. */
  transport?: TranscriptActivityTransport | null;
};

/** The name column, shared by every row and by the axis, so the tracks line up. */
const NAME_COLUMN = "w-[168px] shrink-0";
/** The talk-time column, right-aligned so the figures stack on their colon. */
const TIME_COLUMN = "w-[42px] shrink-0 text-right";

export function TranscriptActivityPanel({
  activity,
  speakerNames,
  onJumpToTurn,
  transport = null,
}: TranscriptActivityPanelProps) {
  // One switch for everything the transport draws and does.
  const sounding = transport?.isSounding ? transport : null;
  const playedRatio =
    activity.durationSeconds > 0 && sounding !== null
      ? Math.min(1, Math.max(0, sounding.positionSeconds / activity.durationSeconds))
      : 0;

  const seekFromPointer = useCallback(
    (element: HTMLElement, clientX: number) => {
      if (sounding === null || activity.durationSeconds <= 0) return;
      const bounds = element.getBoundingClientRect();
      if (bounds.width <= 0) return;
      const fraction = Math.min(1, Math.max(0, (clientX - bounds.left) / bounds.width));
      sounding.onSeek(fraction * activity.durationSeconds);
    },
    [activity.durationSeconds, sounding]
  );

  return (
    <div
      data-testid="transcript-activity-panel"
      className="flex shrink-0 flex-col gap-2 border-border border-b px-5 py-3 min-[840px]:px-6"
    >
      <div className="relative flex flex-col gap-2">
        {activity.rows.map((row, index) => (
          <ActivityRow
            key={row.speaker ?? "unlabelled"}
            row={row}
            speakerNames={speakerNames}
            // The tooltip opens below its segment everywhere but the last row, where there is
            // nothing under the panel to open into.
            tooltipBelow={index < activity.rows.length - 1}
            onJumpToTurn={onJumpToTurn}
            transport={sounding}
            onSeekFromPointer={seekFromPointer}
          />
        ))}
        {sounding === null ? null : (
          <Playhead
            ratio={playedRatio}
            transport={sounding}
            durationSeconds={activity.durationSeconds}
          />
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className={cn(NAME_COLUMN, "text-[10px] text-muted-foreground")}>
          {formatSpeakerActivityTime(activity.durationSeconds)} total
        </span>
        <div className="relative h-[15px] flex-1">
          {activity.ticks.map((tick, index) => (
            <span
              key={tick.seconds}
              className={cn(
                "absolute top-0 text-[10px] text-muted-foreground",
                index === 0 && "translate-x-0",
                index === activity.ticks.length - 1 && "-translate-x-full",
                index > 0 && index < activity.ticks.length - 1 && "-translate-x-1/2"
              )}
              style={{ left: `${tick.ratio * 100}%` }}
            >
              {tick.label}
            </span>
          ))}
        </div>
        <span className={TIME_COLUMN} aria-hidden />
      </div>
    </div>
  );
}

/**
 * The clock, drawn over every track at once, for as long as sound is coming out.
 *
 * An overlay in the tracks' own three-column geometry rather than a line per row: the playhead is
 * one moment in the recording, and drawn per row it would be N lines a reader has to believe are
 * aligned. It is lighter than the type on purpose — it marks a position, it is not a thing to
 * read — and everything past it is knocked back with the page's own ground at low alpha, which is
 * the whole of the "how far through am I" signal. No second progress bar.
 *
 * The cap is the only part of the overlay that takes the pointer: dragging it scrubs, and the
 * drag is silent for the same reason the footer's is.
 */
function Playhead({
  ratio,
  transport,
  durationSeconds,
}: {
  ratio: number;
  transport: TranscriptActivityTransport;
  durationSeconds: number;
}) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);

  const seekFromPointer = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (track === null || durationSeconds <= 0) return;
      const bounds = track.getBoundingClientRect();
      if (bounds.width <= 0) return;
      const fraction = Math.min(1, Math.max(0, (clientX - bounds.left) / bounds.width));
      transport.onSeek(fraction * durationSeconds);
    },
    [durationSeconds, transport]
  );

  return (
    <div className="pointer-events-none absolute inset-0 flex items-stretch gap-3">
      <span className={NAME_COLUMN} aria-hidden />
      <div ref={trackRef} className="relative flex-1">
        <span
          data-testid="transcript-playhead-unplayed"
          aria-hidden
          className="absolute inset-y-0 right-0 bg-background/65"
          style={{ left: `${ratio * 100}%` }}
        />
        <span
          data-testid="transcript-playhead"
          aria-hidden
          className="absolute inset-y-0 w-[1.5px] bg-neutral-muted-fg"
          style={{ left: `${ratio * 100}%` }}
        />
        <span
          data-testid="transcript-playhead-cap"
          className="-top-1 -translate-x-1/2 pointer-events-auto absolute h-1 w-2 cursor-ew-resize bg-neutral-muted-fg"
          style={{ left: `${ratio * 100}%` }}
          onPointerDown={(event: ReactPointerEvent<HTMLSpanElement>) => {
            event.preventDefault();
            event.stopPropagation();
            draggingRef.current = true;
            event.currentTarget.setPointerCapture(event.pointerId);
            transport.onScrubStart();
          }}
          onPointerMove={(event: ReactPointerEvent<HTMLSpanElement>) => {
            if (!draggingRef.current) return;
            seekFromPointer(event.clientX);
          }}
          onPointerUp={(event: ReactPointerEvent<HTMLSpanElement>) => {
            if (!draggingRef.current) return;
            draggingRef.current = false;
            seekFromPointer(event.clientX);
            transport.onScrubEnd();
          }}
          onPointerCancel={() => {
            if (!draggingRef.current) return;
            draggingRef.current = false;
            transport.onScrubEnd();
          }}
        />
      </div>
      <span className={TIME_COLUMN} aria-hidden />
    </div>
  );
}

function ActivityRow({
  row,
  speakerNames,
  tooltipBelow,
  onJumpToTurn,
  transport,
  onSeekFromPointer,
}: {
  row: SpeakerActivityRow;
  speakerNames: TranscriptSpeakerNames;
  tooltipBelow: boolean;
  onJumpToTurn: (turnKey: number) => void;
  /** The transport while sound is coming out, and `null` at every other moment. */
  transport: TranscriptActivityTransport | null;
  onSeekFromPointer: (element: HTMLElement, clientX: number) => void;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const label =
    row.speaker === null
      ? "Whole transcript"
      : transcriptSpeakerDisplayLabel(row.speaker, speakerNames);

  return (
    <div
      data-testid="transcript-activity-row"
      data-speaker={row.speaker ?? undefined}
      className="flex items-center gap-3"
      style={speakerPaletteStyle(row.speaker ?? 1)}
    >
      <div className={cn(NAME_COLUMN, "flex items-center gap-1.5")}>
        <span className="h-3 w-[3px] shrink-0 bg-(--speaker-bar)" aria-hidden />
        <span className="truncate font-medium text-[11.5px]">{label}</span>
      </div>
      <div
        data-testid="transcript-activity-track"
        className="relative h-2 flex-1 bg-neutral-muted"
        onPointerDown={
          transport === null
            ? undefined
            : (event: ReactPointerEvent<HTMLDivElement>) => {
                onSeekFromPointer(event.currentTarget, event.clientX);
              }
        }
      >
        {row.segments.map((segment, index) => (
          <button
            key={segment.turnKey}
            type="button"
            data-testid="transcript-activity-segment"
            className={cn(
              "absolute cursor-pointer bg-(--speaker-bar) hover:opacity-80",
              // A hovered segment lifts out of its track so the tooltip has something to point at.
              hovered === index ? "-top-0.5 h-3" : "top-0 h-full"
            )}
            style={{
              left: `${segment.startRatio * 100}%`,
              width: `${segment.widthRatio * 100}%`,
            }}
            // A segment is a jump whatever the play state, so it is named one. Where the recording
            // is sounding the press seeks as well — the track under it answers the pointer — but
            // what the reader asked for is the words, and that is what the label promises.
            aria-label={`${label}, ${formatSpeakerActivityRange(segment)}. Jump to this point.`}
            onMouseEnter={() => setHovered(index)}
            onMouseLeave={() => setHovered((current) => (current === index ? null : current))}
            onFocus={() => setHovered(index)}
            onBlur={() => setHovered((current) => (current === index ? null : current))}
            onClick={() => onJumpToTurn(segment.turnKey)}
          />
        ))}
        {hovered === null || row.segments[hovered] === undefined ? null : (
          <span
            data-testid="transcript-activity-tooltip"
            className={cn(
              "pointer-events-none absolute z-10 flex items-center gap-1.5 whitespace-nowrap border border-neutral-border bg-popover px-2 py-1 shadow-md",
              tooltipBelow ? "top-[calc(100%+6px)]" : "bottom-[calc(100%+6px)]"
            )}
            style={{ left: `${row.segments[hovered].startRatio * 100}%` }}
          >
            <span className="font-medium text-[10.5px] text-popover-foreground">
              {formatSpeakerActivityRange(row.segments[hovered])}
            </span>
            <span className="text-[10.5px] text-neutral-muted-fg">Jump to this point</span>
          </span>
        )}
      </div>
      <span className={cn(TIME_COLUMN, "text-[10.5px] text-muted-foreground")}>
        {formatSpeakerActivityTime(row.talkSeconds)}
      </span>
    </div>
  );
}
